/**
 * @module lib/outputEngine
 * @description Image / share outputs for any rendered card element.
 * Each function has one job and no UI, so the admin, the print studio and
 * the public card view compose the same units. Printing lives in lib/print.
 */
import { toBlob, toCanvas } from "html-to-image";
import { PRINT_DPI, PRINT_SIZES } from "../config/print";

/** PNG downloads are sized for the largest print (6 × 9 in at 300 dpi = 2700 px wide). */
const EXPORT_WIDTH_PX = PRINT_SIZES["6x9"].widthIn * PRINT_DPI;

/**
 * Waits until every image inside `element` has decoded, so a capture or a
 * print never shows a blank photo frame.
 */
export async function waitForImages(element: HTMLElement): Promise<void> {
    const images = Array.from(element.querySelectorAll("img"));
    await Promise.all(
        images.map((img) =>
            img.decode().catch((error: unknown) => {
                // A failed image is reported, not swallowed: it would print as an empty frame.
                console.error("[outputEngine] Image failed to load", img.currentSrc || img.src, error);
            })
        )
    );
}

/**
 * Renders a DOM element to a canvas exactly `widthPx` wide.
 * External integration: html-to-image clones the node into an SVG
 * foreignObject; fonts and images must be same-origin (they are, self-hosted).
 */
export async function renderElementToCanvas(element: HTMLElement, widthPx: number): Promise<HTMLCanvasElement> {
    await document.fonts.ready;
    await waitForImages(element);
    const pixelRatio = widthPx / element.getBoundingClientRect().width;
    return toCanvas(element, { pixelRatio, cacheBust: true });
}

/** Width of the image attached to native shares: sharp on any phone, light enough to render on one. */
export const SHARE_IMAGE_WIDTH_PX = 1600;

/** Renders a DOM element to a PNG blob, by default at print resolution. */
export async function renderElementToPng(element: HTMLElement, widthPx: number = EXPORT_WIDTH_PX): Promise<Blob> {
    await document.fonts.ready;
    await waitForImages(element);
    const pixelRatio = widthPx / element.getBoundingClientRect().width;
    const blob = await toBlob(element, { pixelRatio, cacheBust: true });
    if (!blob) throw new Error("Image export returned no data.");
    return blob;
}

/** Triggers a browser download for a blob. */
export function downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement("a"), { href: url, download: filename });
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export interface SharePayload {
    title: string;
    text: string;
    url: string;
}

/** Social / email share URLs. Pure — no side-effects. */
export function shareLinks({ title, text, url }: SharePayload): Record<"email" | "facebook" | "x" | "linkedin" | "whatsapp", string> {
    const u = encodeURIComponent(url);
    const t = encodeURIComponent(text);
    return {
        email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${text}\n\n${url}`)}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
        x: `https://twitter.com/intent/tweet?text=${t}&url=${u}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
        whatsapp: `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`,
    };
}

/** Outcome of a native-share attempt, so the UI can say exactly what happened. */
export type NativeShareResult = "shared" | "cancelled" | "unsupported" | "blocked" | "failed";

/**
 * Opens the device's share sheet (phones, tablets, Safari and Edge on
 * desktop), attaching the card image when the platform accepts files.
 *
 * // BUG FIX: navigator.share() is now called synchronously inside the click.
 * What caused this: 1A awaited a ~1–3 s image render before calling
 * share(). Browsers only open the share sheet during the user's tap
 * ("transient activation"); after the await, Safari on iPhone rejects the
 * call with NotAllowedError, so "Share" appeared to do nothing.
 * Why chosen: the image is rendered ahead of time (OutputBar) and passed in
 * ready-made; if it isn't ready yet, the link alone is shared.
 * Side-effects: a very fast first tap shares the link without the picture.
 */
export async function nativeShare(payload: SharePayload, image: Blob | null): Promise<NativeShareResult> {
    if (typeof navigator.share !== "function") return "unsupported";
    const file = image ? new File([image], "to-jamaica-with-love.png", { type: "image/png" }) : null;
    const data: ShareData = file && navigator.canShare?.({ files: [file] }) ? { ...payload, files: [file] } : payload;
    try {
        await navigator.share(data);
        return "shared";
    } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return "cancelled";
        if (error instanceof DOMException && error.name === "NotAllowedError") return "blocked";
        console.error("[outputEngine] Native share failed", error);
        return "failed";
    }
}
